import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Permission } from '../types/user.types';

export const usePermissions = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('usePermissions must be used within AuthProvider');
  }
  const { user } = context;

  const hasPermission = (permission: Permission): boolean => {
    if (!user) return false;

    // Admin role has all permissions
    if (user.role === 'admin') return true;

    // Check if user has specific permission
    return user.permissions?.includes(permission) || false;
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const canApproveCustomers = (): boolean => {
    return hasPermission('approve_customers');
  };

  const canApproveSuppliers = (): boolean => {
    return hasPermission('approve_suppliers');
  };

  const canManageUsers = (): boolean => {
    return hasPermission('manage_users');
  };

  const isAdmin = (): boolean => {
    return user?.role === 'admin';
  };

  const isApprover = (): boolean => {
    return user?.role === 'approver';
  };

  const isViewer = (): boolean => {
    return user?.role === 'viewer';
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canApproveCustomers,
    canApproveSuppliers,
    canManageUsers,
    isAdmin,
    isApprover,
    isViewer,
    userPermissions: user?.permissions || [],
    userRole: user?.role,
  };
};
